import { Injectable, inject } from '@angular/core';
import { MultiselectItem } from '@ui/aos-lib';
import { combineLatest, catchError, of, switchMap, take, Observable } from 'rxjs';
import { Store } from '@ngrx/store';

import { ReportGroupFilterListingService } from 'src/app/services/api/report-group-filter/report-group-filter-listing.service';
import { translationKeysMap } from 'src/app/services/translate/translate.key-mapper';
import {
  COMMODITIES,
  DEVICE_TYPES,
  STATUSES,
  HEALTH,
  LAST_HEARD,
  BATTERY_STATUS,
  TRANSMISSION_READ_RATE,
  VALID_TRANSMISSION_READ_RATE,
  ON_DEMAND_READ_STATUS,
  RDD_CONNECTED,
  VALVE_STATUS
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  FilterModel,
  DeviceTypeGroup,
  FilterModels,
  ListingFiltersSource
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import {
  isSingleCommodityClient,
  selectNonCommunicationCommodities
} from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable()
export class MeteringListingFiltersSourceService implements ListingFiltersSource {
  private readonly store = inject(Store);
  readonly reportGroupFilterService = inject(ReportGroupFilterListingService);
  readonly #isSingleCommodityClient = this.store.selectSignal(isSingleCommodityClient);

  readonly #applicable = 'AMI Health Dashboard';

  getAll(): Observable<FilterModels> {
    return combineLatest([
      this.store.select(selectNonCommunicationCommodities).pipe(take(1)),
      this.getReportGroup()
    ]).pipe(
      switchMap(([commoditiesItems, reportGroupFilter]) => {
        const commoditiesMultiselectItems: MultiselectItem[] = commoditiesItems.map(item => ({
          text: translationKeysMap.get(item) || item,
          value: item
        }));
        const commoditiesFilter: FilterModel = {
          ...COMMODITIES,
          items: commoditiesMultiselectItems,
          selected: commoditiesMultiselectItems
        };
        const filters = [
          reportGroupFilter,
          DEVICE_TYPES[DeviceTypeGroup.Metering],
          ...(this.#isSingleCommodityClient() ? [] : [commoditiesFilter]),
          STATUSES,
          HEALTH,
          LAST_HEARD,
          BATTERY_STATUS,
          TRANSMISSION_READ_RATE,
          VALID_TRANSMISSION_READ_RATE,
          ON_DEMAND_READ_STATUS,
          VALVE_STATUS,
          RDD_CONNECTED
        ];

        return of(filters);
      }),
      catchError(error => {
        console.error(error);
        return of([]);
      })
    );
  }

  getReportGroup(): Observable<FilterModel> {
    return this.reportGroupFilterService.getFilter(this.#applicable);
  }
}
